import clsx from "clsx";
import { FC } from "react";

import { GroupMenuButton } from "components/materials/page/interaction-mode/GroupMenuButton";
import { StudentInteractionModeView } from "components/materials/page/interaction-mode/StudentInteractionModeView";
import {
  CHIP_BASE_CLASSNAME,
  INTERACTION_MODE_COLORS,
  INTERACTION_MODE_ICONS,
  PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE,
  PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW,
} from "components/materials/page/interaction-mode/helpers";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { InteractionMode, PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { ClickEvent, Menu, MenuItem } from "mds/components/Menu";
import { Tooltip } from "mds/components/Tooltip";
import { ChevronDownIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectHasSubmissionsForPageId, selectIsEditModeEnabled } from "store/selectors";
import { ComponentEventProperties } from "utils/amplitude";
import { isGroupPage } from "utils/page-utils";

export type InteractionModeMenuProps = {
  page: PageType;
  onAuthorClick: (interactionMode: InteractionMode) => void;
  onStudentGroupCardToggle?: () => void;
  readOnly?: boolean;
  pageGroupIndex: number;
};

export const InteractionModeMenu: FC<InteractionModeMenuProps> = ({
  page,
  onAuthorClick,
  onStudentGroupCardToggle,
  readOnly = false,
  pageGroupIndex,
}) => {
  const { canParticipateInCourse } = useCapabilities();
  const hasSubmissions = useAppSelector((s) => selectHasSubmissionsForPageId(s, page.id));
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const isAssessing = useIsAssessing();
  const isReadOnly = readOnly;
  const isDisabled = hasSubmissions || isAssessing;

  if (page.interaction_mode === InteractionMode.STATIC) {
    return null;
  }

  if (canParticipateInCourse) {
    return (
      <StudentInteractionModeView
        page={page}
        pageGroupIndex={pageGroupIndex}
        onStudentGroupCardToggle={onStudentGroupCardToggle}
      />
    );
  }

  const tooltipText =
    hasSubmissions && isEditModeEnabled ? t("tooltip.group_config_has_submissions") : "";

  if (!isGroupPage(page) && (isReadOnly || isDisabled)) {
    if (page.interaction_mode === InteractionMode.SHARED_CLASS) {
      return (
        <Tooltip text={tooltipText}>
          <IconText
            className={`${CHIP_BASE_CLASSNAME} ${INTERACTION_MODE_COLORS[page.interaction_mode]}`}
            containerQueryClassNameHide={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE}
            containerQueryClassNameShow={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW}
            iconStart={INTERACTION_MODE_ICONS[page.interaction_mode]}
            shortText={t("page_interaction_mode_menu.interaction_mode.all_short")}
            text={t("page_interaction_mode_menu.interaction_mode.all")}
          />
        </Tooltip>
      );
    }

    if (page.interaction_mode === InteractionMode.INDIVIDUAL) {
      return (
        <Tooltip text={tooltipText}>
          <IconText
            className={`${CHIP_BASE_CLASSNAME} ${INTERACTION_MODE_COLORS[page.interaction_mode]}`}
            containerQueryClassNameHide={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE}
            containerQueryClassNameShow={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW}
            iconStart={INTERACTION_MODE_ICONS[page.interaction_mode]}
            text={t("page_interaction_mode_menu.interaction_mode.individual")}
          />
        </Tooltip>
      );
    }
  }

  const onItemClick = (e: ClickEvent) => {
    const interactionMode = e.value as InteractionMode;
    onAuthorClick(interactionMode);
  };

  const MenuButton = !isGroupPage(page) ? (
    <Button
      className="gap-1"
      kind={
        page.interaction_mode === InteractionMode.INDIVIDUAL
          ? "chipBlue"
          : page.interaction_mode === InteractionMode.SHARED_CLASS
            ? "chipMoss"
            : "chipHibiscus" // shouldn't get here
      }
      size="xs"
    >
      <IconText
        className="gap-1.5 text-xs"
        containerQueryClassNameHide={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE}
        containerQueryClassNameShow={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW}
        iconStart={INTERACTION_MODE_ICONS[page.interaction_mode]}
        shortText={
          page.interaction_mode === InteractionMode.SHARED_CLASS
            ? t("page_interaction_mode_menu.interaction_mode.all_short")
            : undefined
        }
        text={t(`page_interaction_mode_menu.interaction_mode.${page.interaction_mode}`)}
      />
      <ChevronDownIcon size="xs" />
    </Button>
  ) : (
    <GroupMenuButton
      page={page}
      pageGroupIndex={pageGroupIndex}
      restricted={isReadOnly || isDisabled}
      onGroupConfigCardToggle={onAuthorClick}
      onStudentGroupCardToggle={onStudentGroupCardToggle}
    />
  );

  return (
    <div>
      <Menu
        menuButton={MenuButton}
        title={t("page_interaction_mode_menu.change_page_interaction_mode")}
        onItemClick={onItemClick}
      >
        {!isReadOnly && !isDisabled && (
          <>
            <InteractionModeMenuItem
              interactionMode={InteractionMode.INDIVIDUAL}
              key="1"
              page={page}
            />
            <InteractionModeMenuItem
              interactionMode={InteractionMode.SHARED_CLASS}
              key="2"
              page={page}
            />
            <InteractionModeMenuItem
              interactionMode={
                page.interaction_mode === InteractionMode.SELF_ASSIGN
                  ? page.interaction_mode
                  : InteractionMode.ASSIGN_STUDENTS
              }
              key="3"
              page={page}
            />
          </>
        )}
      </Menu>
    </div>
  );
};

type InteractionModeMenuItemProps = ComponentEventProperties & {
  interactionMode: InteractionMode;
  page: PageType;
};

const InteractionModeMenuItem: FC<InteractionModeMenuItemProps> = ({ interactionMode, page }) => {
  const checked = interactionMode === page.interaction_mode;

  if (interactionMode === InteractionMode.STATIC) {
    return null;
  }

  return (
    <MenuItem
      checked={checked}
      className={clsx({ selected: checked })}
      eventName="Page type updated"
      eventProps={{
        pageInteractionMode: interactionMode,
        pageTitle: page.title,
        pageId: page.id,
      }}
      value={interactionMode}
    >
      <IconText
        iconStart={INTERACTION_MODE_ICONS[interactionMode]}
        text={t(`page_interaction_mode_menu.interaction_mode.${interactionMode}`)}
      />
    </MenuItem>
  );
};
